"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _react = _interopRequireDefault(require("react"));
var _zxcvbn = _interopRequireDefault(require("zxcvbn"));
var _InputText = _interopRequireDefault(require("../InputText"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _messages = require("./messages");
var _getLanguage = _interopRequireDefault(require("../../utils/getLanguage"));
var _locales = require("../../utils/locales");
var _PasswordInput_css = _interopRequireDefault(require("./PasswordInput_css"));
var _appendCss = _interopRequireDefault(require("../../utils/appendCss"));
const PasswordInput = _ref => {
  let {
    additionalProps,
    handleChange,
    value,
    className,
    isRequired,
    isError,
    errorMsg,
    userLibrary = [],
    validatePhraseStr,
    complexityTarget,
    testId
  } = _ref;
  (0, _appendCss.default)("arc_passwordInput", _PasswordInput_css.default);
  const [phraseStrength, setPhraseStrength] = _react.default.useState(25);
  const [phraseFeedback, setPhraseFeedback] = _react.default.useState();
  const [phraseArray, setPhraseArray] = _react.default.useState([]);
  const [phraseStrengthLabel, setPhraseStrengthLabel] = _react.default.useState("Weak");
  const [obfuscatePhrase, setObfuscatePhrase] = _react.default.useState(true);
  const [valid, setValid] = _react.default.useState(false);
  const language = (0, _getLanguage.default)();
  const determineIcon = () => {
    return obfuscatePhrase ? "visibility" : "visibility_off";
  };
  const handleIconClick = () => {
    setObfuscatePhrase(!obfuscatePhrase);
  };
  const convertStringsToKeys = (obj, searchValues) => {
    const result = [];
    for (const [key, value] of Object.entries(obj)) {
      if (searchValues.includes(value)) {
        result.push(key);
      }
    }
    setPhraseArray(result);
  };
  const handlePhraseStrength = value => {
    const result = (0, _zxcvbn.default)(value, userLibrary);
    let phraseStrengthLabel = "";
    if (result.score >= 2) {
      setPhraseStrength(result.score * 25);
    } else {
      setPhraseStrength(25);
    }

    // Callback to return the password strength
    if (Boolean(handleChange)) handleChange(value, result.score);
    if (Boolean(result.feedback)) setPhraseFeedback(result.feedback);

    // validates the strength of the phrase and sets a boolean for Input to use
    if (validatePhraseStr) result.score >= complexityTarget ? setValid(true) : setValid(false);

    // Converts the english suggestions array from zxvbn to an array of message keys, so we can pull a translated string from the messages.js
    if (Boolean(result.feedback.suggestions)) convertStringsToKeys(_messages.messages[_locales.locales.en], result.feedback.suggestions);
    switch (result.score) {
      case 2:
        phraseStrengthLabel = _messages.messages[language].password_fair;
        break;
      case 3:
        phraseStrengthLabel = _messages.messages[language].password_strong;
        break;
      case 4:
        phraseStrengthLabel = _messages.messages[language].password_secure;
        break;
      default:
        phraseStrengthLabel = _messages.messages[language].password_weak;
    }
    setPhraseStrengthLabel(phraseStrengthLabel);
  };
  return /*#__PURE__*/_react.default.createElement("div", {
    className: "phraseStr-wrapper ".concat(className)
  }, /*#__PURE__*/_react.default.createElement("div", {
    className: "phraseStr-inputWrapper"
  }, /*#__PURE__*/_react.default.createElement(_InputText.default, (0, _extends2.default)({
    label: _messages.messages[language].password,
    type: obfuscatePhrase ? "password" : "text",
    name: "passwordInput",
    id: "passwordInput",
    "data-testid": testId,
    icon: determineIcon(),
    iconTheme: "outlined",
    onClickIcon: () => handleIconClick(),
    onChange: e => handlePhraseStrength(e.target.value),
    value: value,
    isRequired: isRequired,
    isError: isError,
    errorMsg: errorMsg,
    maxLength: 100,
    validate: validatePhraseStr ? valid : undefined
  }, additionalProps))), Boolean(value) && /*#__PURE__*/_react.default.createElement("div", {
    className: "phraseStr-meterWrapper phraseStr-".concat(phraseStrength),
    role: "progressbar",
    "aria-valuemin": "0",
    "aria-valuemax": "100",
    "aria-valuenow": phraseStrength,
    "aria-labelledby": "password-strength-description",
    style: {
      width: "".concat(phraseStrength, "%")
    }
  }, /*#__PURE__*/_react.default.createElement("p", {
    className: "t-label-small",
    id: "phraseStr-label"
  }, phraseStrengthLabel)), (Boolean(phraseArray.length) || (phraseFeedback === null || phraseFeedback === void 0 ? void 0 : phraseFeedback.warning)) && Boolean(value) && /*#__PURE__*/_react.default.createElement("div", {
    className: "phraseStr-suggestionsWrapper"
  }, /*#__PURE__*/_react.default.createElement("p", {
    className: "phraseStr-suggestionsLabel t-label-med"
  }, _messages.messages[language].password_suggestions), /*#__PURE__*/_react.default.createElement("ul", null, (phraseFeedback === null || phraseFeedback === void 0 ? void 0 : phraseFeedback.warning) && /*#__PURE__*/_react.default.createElement("li", {
    className: "t-body-small"
  }, phraseFeedback.warning), phraseArray.map((key, index) => /*#__PURE__*/_react.default.createElement("li", {
    className: "t-body-small",
    key: "pw-msg-".concat(index)
  }, _messages.messages[language][key])))));
};
PasswordInput.propTypes = {
  /** Additional props to be added to the InputText component */
  additionalProps: _propTypes.default.object,
  /** Sets classname on the component wrapper */
  className: _propTypes.default.string,
  /** Callback to return if InputText is valid */
  isValid: _propTypes.default.func,
  /** Sets the isError prop on the InputText */
  isError: _propTypes.default.bool,
  /** Sets the errorMsg prop on the InputText */
  errorMsg: _propTypes.default.string,
  /** A library of words to add to the password dictionary, things like the users name/email address, company name, etc */
  userLibrary: _propTypes.default.array,
  /** Callback to handle the input change, returns input value and password complexity */
  handleChange: _propTypes.default.func.isRequired,
  /** Sets the value of the InputText */
  value: _propTypes.default.string.isRequired,
  /** Sets the component to validate the input - if used it requires a target complexity */
  validatePhraseStr: _propTypes.default.bool,
  /** Sets the target complexity for the password */
  complexityTarget: _propTypes.default.oneOf([0, 1, 2, 3, 4]),
  /** Sets the testId on the Input */
  testId: _propTypes.default.string
};
var _default = exports.default = PasswordInput;